<template>
  <div class="transaction-list-container">
    <div class="card">
      <h2>交易记录</h2>
      
      <div class="filters mb-4">
        <div class="filter-row">
          <el-select v-model="filters.transType" placeholder="交易类型" class="filter-item">
            <el-option label="全部类型" :value="0" />
            <el-option label="充值" :value="1" />
            <el-option label="使用" :value="2" />
            <el-option label="赠送" :value="3" />
          </el-select>
          <el-input v-model="filters.orderNo" placeholder="订单号" class="filter-item" />
          <el-button type="primary" @click="loadTransactions" class="filter-item">
            <el-icon><Search /></el-icon>
            查找
          </el-button>
        </div>
      </div>

      <el-table
        :data="transactions"
        style="width: 100%"
        border
        v-loading="loading"
      >
        <el-table-column
          prop="createdAt"
          label="时间"
          width="180"
        >
          <template #default="{ row }">
            {{ formatDate(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="payType"
          label="类型"
          width="120"
        >
          <template #default="{ row }">
            <el-tag :type="getTypeTagType(row.payType)">
              {{ row.payType }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          prop="orderNo"
          label="订单号"
          width="180"
        />
        <el-table-column
          prop="detail"
          label="详情"
        />
        <el-table-column
          prop="amount"
          label="金额"
          width="120"
        >
          <template #default="{ row }">
            <span :class="getAmountClass(row.amount)">¥{{ formatAmount(row.amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="balance"
          label="余额"
          width="120"
        >
          <template #default="{ row }">
            ¥{{ formatAmount(row.balance) }}
          </template>
        </el-table-column>
      </el-table>

      <AdminPagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :total="total"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>
  </div>
</template>

<script>
import { goToLogin } from '@/utils/loginFlow'
import { ref, onMounted } from 'vue'
import axios from 'axios'
import { Search } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { useRouter } from 'vue-router'
import AdminPagination from '@/components/AdminPagination.vue'

export default {
  name: 'TransactionListView',
  components: {
    Search,
    AdminPagination
  },
  setup() {
    const transactions = ref([])
    const total = ref(0)
    const currentPage = ref(1)
    const pageSize = ref(20)
    const loading = ref(false)
    const filters = ref({
      transType: 0,
      orderNo: '',
      currencyCode: 'CNY',
      beginCreatedAt: 0,
      endCreatedAt: 0,
      orgId: 0
    })

    const router = useRouter()

    // 检查是否登录
    const checkLogin = () => {
      if (!localStorage.getItem('jwt_token')) {
        goToLogin(router)
        return false
      }
      return true
    }

    const loadTransactions = async () => {
      try {
        loading.value = true
        const token = localStorage.getItem('jwt_token')
        const response = await axios.post('/api/v1/transactions/list', {
          page: currentPage.value,
          pageSize: pageSize.value,
          ...filters.value
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (response.data.code === 200) {
          transactions.value = response.data.data.trans
          total.value = response.data.data.total
        } else {
          ElMessage.error(response.data.message || '获取交易记录失败')
        }
      } catch (error) {
        console.error('Failed to load transactions:', error)
        ElMessage.error('获取交易记录失败')
      } finally {
        loading.value = false
      }
    }

    const handlePageChange = (page) => {
      currentPage.value = page
      loadTransactions()
    }

    const handleSizeChange = (size) => {
      pageSize.value = size
      currentPage.value = 1
      loadTransactions()
    }

    const formatDate = (timestamp) => {
      return new Date(timestamp * 1000).toLocaleString()
    }

    const formatAmount = (amount) => {
      if (!amount && amount !== 0) return '0.00'
      return (Number(amount) / 100).toFixed(2)
    }

    const getTypeTagType = (type) => {
      const types = {
        'Recharge': 'success',
        'Usage': 'primary',
        'Gift': 'warning'
      }
      return types[type] || 'info'
    }

    const getAmountClass = (amount) => {
      return amount > 0 ? 'text-success' : 'text-danger'
    }

    onMounted(() => {
      // 检查是否登录
      if (!checkLogin()) {
        return
      }
      loadTransactions()
    })

    return {
      transactions,
      total,
      currentPage,
      pageSize,
      loading,
      filters,
      loadTransactions,
      handlePageChange,
      handleSizeChange,
      formatDate,
      formatAmount,
      getTypeTagType,
      getAmountClass
    }
  }
}
</script>

<style scoped>
.transaction-list-container {
  padding: 20px;
  max-width: 1800px;
  margin: 0 auto;
}

.card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
}

.filters {
  margin-bottom: 20px;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.text-success {
  color: var(--el-color-success);
}

.text-danger {
  color: var(--el-color-danger);
}

:deep(.el-table) {
  --el-table-border-color: var(--el-border-color-lighter);
  --el-table-header-bg-color: var(--el-fill-color-light);
}

:deep(.el-table th) {
  font-weight: 600;
  color: var(--el-text-color-primary);
}

:deep(.el-table td) {
  padding: 12px 0;
}

:deep(.el-tag) {
  font-weight: 500;
}

@media (max-width: 768px) {
  .transaction-list-container {
    padding: 10px;
  }

  .card {
    padding: 15px;
  }
}

.filter-row {
  display: flex;
  gap: 16px;
  align-items: center;
}

.filter-item {
  flex: 1;
  max-width: 200px;
}

.filter-item:last-child {
  flex: 0 0 auto;
  max-width: none;
}
</style> 